import DatePicker from 'react-datepicker';
import { useForm } from 'react-hook-form';
import styled from 'styled-components';
import { useEffect, useState } from 'react';
import { useRecoilValue } from 'recoil';
import { authState } from '../../../atoms/authState';
import { zodResolver } from '@hookform/resolvers/zod';
import { inquiryRegistrationSchema } from '../../../schema/inquirySchema';
import { useHistory, useParams } from 'react-router-dom';
import { AlertModal } from '../../../components/modal/alert.modal';
import { fetchInquiryDetail, updateInquiry } from '../../../api/inquiry';

interface InquiryDto {
  title: string;
  question: string;
}

interface InquiryDetailDtoRx {
  passid: string;
  phone: string;
  createdAt: string;
  title: string;
  question: string;
  answer: string;
}

const InquiryUpdate = () => {
  const { id } = useParams<{ id: string }>();
  const auth = useRecoilValue(authState);

  const today = new Date();
  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<InquiryDto>({
    resolver: zodResolver(inquiryRegistrationSchema),
  });

  const [detailData, setDetailData] = useState<InquiryDetailDtoRx>();

  // 모달
  const history = useHistory();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMessage, setModalMessage] = useState('');
  const [navigateOnClose, setNavigateOnClose] = useState(false); // 페이지 이동 여부를 추적

  const [targetDate, setTargetDate] = useState<Date>(today);

  const handleModalClose = () => {
    setIsModalOpen(false);
    if (navigateOnClose) {
      history.push({
        pathname: '/inquiry',
      });
    }
  };

  const onSubmit = async (data: InquiryDto) => {
    try {
      const response = await updateInquiry(Number(id), data); // 데이터 전송
      if (response.statusCode === 200) {
        setModalMessage(response.message);
        setIsModalOpen(true);
        setNavigateOnClose(true);
      } else {
        setModalMessage('문의 수정에 실패했습니다.');
        setIsModalOpen(true);
        setNavigateOnClose(false);
      }
    } catch (error) {
      console.error('Failed to update order:', error);
    }
  };

  // 데이터 가져오기
  useEffect(() => {
    const getInquiryDetail = async () => {
      try {
        const response = await fetchInquiryDetail(Number(id));

        setDetailData(response);

        // 데이터 로드 후 input 필드에 값 설정
        setValue('title', response.title);
        setValue('question', response.question);
        console.log(response);
      } catch (error) {
        console.error('Error fetching order detail:', error);
      }
    };

    getInquiryDetail();
  }, [id]);

  return (
    <Container>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="car-info">
          <ul>
            <li>
              <div className="label">
                {auth.userType === 'driver' ? '차량 번호' : '아이디'}
              </div>
              <div className="value">{detailData?.passid}</div>
            </li>
            <li>
              <div className="label">휴대폰 번호</div>
              <div className="value">{detailData?.phone}</div>
            </li>
            <li>
              <div className="label">등록일</div>
              <div className="value">{detailData?.createdAt}</div>
            </li>
          </ul>
        </div>
        <div className="contents">
          <div className="title">
            제목 <span>*</span>
          </div>
          <div className="input-box">
            <input type="text" {...register('title')} />
            {errors.title && <ValTxt>{String(errors.title.message)}</ValTxt>}
          </div>
          <div className="title">
            내용 <span>*</span>
          </div>
          <div className="text-box-wrap">
            <div className="text-box">
              <textarea
                {...register('question')}
                placeholder="일감 구함에 등록할 내용을 입력해주세요."
              ></textarea>
            </div>
            {errors.question && (
              <ValTxt>{String(errors.question.message)}</ValTxt>
            )}
          </div>
        </div>
        <Btn>수정 하기</Btn>
      </form>

      {isModalOpen && (
        <AlertModal
          handleConfirmClose={handleModalClose}
          confirmText={modalMessage}
          onClick={handleModalClose}
        />
      )}
    </Container>
  );
};

export default InquiryUpdate;

// style
const Container = styled.div`
  height: 100%;
  padding: 0 1.5rem 11.4rem;
  form {
    padding: 2.4rem 0;
    height: 100%;
    .car-info {
      padding: 2.4rem 2.4rem 2.8rem;
      border-radius: 1.6rem;
      box-shadow: 0 2px 5px 3px rgba(0, 0, 0, 0.05);
      position: relative;
      ul {
        display: flex;
        flex-direction: column;
        gap: 1.2rem;
        li {
          display: flex;
          gap: 2.4rem;
          align-items: center;
          .label {
            min-width: 5.6rem;
            font-size: 1.2rem;
            font-weight: 600;
            color: ${(props) => props.theme.textAAAColor};
            line-height: 1.6rem;
          }
          .value {
            font-size: 1.4rem;
            font-weight: 600;
            &.status {
              min-width: 4.2rem;
              color: ${(props) => props.theme.textWhiteColor};
              background-color: ${(props) => props.theme.btnBlueColor};
              display: flex;
              align-items: center;
              justify-content: center;
              padding: 0.4rem;
              font-size: 1.1rem;
              border-radius: 1.1rem;
            }
          }
        }
      }
    }
    .contents {
      display: flex;
      flex-direction: column;
      gap: 0.8rem;
      margin-top: 3.2rem;
      height: calc(100% - 16rem);
      .title {
        font-size: 1.5rem;
        font-weight: 600;
        display: flex;
        align-items: flex-start;
        span {
          color: #ff5151;
          margin-left: 0.2rem;
          transform: translateY(-3px);
        }
      }
      .input-box {
        margin-bottom: 0.7rem;
        input {
          width: 100%;
          border: 1px solid #d6dbe1;
          padding: 1.4rem 1.8rem 1.5rem;
          border-radius: 0.8rem;
        }
      }
      .text-box-wrap {
        flex: 1;
        .text-box {
          flex: 1;
          border-radius: 0.8rem;
          border: 1px solid ${(props) => props.theme.searchBoxBorderedColor};
          overflow: hidden;
          padding: 1.4rem 1.8rem;
          font-size: 1.4rem;
          font-weight: 500;
          height: 100%;
          textarea {
            width: 100%;
            height: 100%;
            flex: 1;
            border: none;
            &::placeholder {
              color: ${(props) => props.theme.searchPlaceholderColor};
              font-size: 1.4rem;
              font-weight: 00;
            }
          }
        }
      }
    }
  }
`;

const Btn = styled.button`
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  min-height: 9.4rem;
  border: none;
  outline: none;
  padding: 1.7rem;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  background-color: ${(props) => props.theme.btnStrongBlueColor};
  color: ${(props) => props.theme.textWhiteColor};
  font-size: 1.8rem;
  font-weight: 600;
`;

const CustomDatePicker = styled(DatePicker)``;

const DatePickerContainer = styled.div`
  display: flex;
  flex-direction: column;
  .inner {
    position: relative;
    width: 100%;
    display: flex;
    align-items: flex-end;
    .search-input {
      font-family: 'SUITE', 'sans-serif';
      width: 100%;
      font-size: 1.4rem;
      font-weight: 600;
      background-color: inherit;
      border: 0px;
      outline: none;
      padding: 0;
      max-width: 12rem;
      &::placeholder {
        color: ${(props) => props.theme.placeholderColor};
      }
    }
    .react-datepicker {
      font-size: 1.4rem;
      font-weight: 400;
      .react-datepicker__header {
        background-color: ${(props) => props.theme.textWhiteColor};
      }
      .react-datepicker__time-container
        .react-datepicker__time
        .react-datepicker__time-box {
        width: 20rem;
      }
      .react-datepicker__time-container {
        width: 20rem;
      }
      .react-datepicker__time-container
        .react-datepicker__time
        .react-datepicker__time-box
        ul.react-datepicker__time-list
        li.react-datepicker__time-list-item {
        width: 100%;
        height: 4.2rem;
        display: flex;
        justify-content: center;
        align-items: center;
        font-size: 1.8rem;
      }
      .react-datepicker__current-month,
      .react-datepicker-time__header,
      .react-datepicker-year-header {
        font-size: 1.4rem;
        height: 20px;
        display: flex;
        align-items: center;
        justify-content: center;
      }
      .react-datepicker__day-name,
      .react-datepicker__day,
      .react-datepicker__time-name {
        width: 3rem;
        line-height: 3rem;
      }
      .react-datepicker__day--selected,
      .react-datepicker__day--in-selecting-range,
      .react-datepicker__day--in-range,
      .react-datepicker__month-text--selected,
      .react-datepicker__month-text--in-selecting-range,
      .react-datepicker__month-text--in-range,
      .react-datepicker__quarter-text--selected,
      .react-datepicker__quarter-text--in-selecting-range,
      .react-datepicker__quarter-text--in-range,
      .react-datepicker__year-text--selected,
      .react-datepicker__year-text--in-selecting-range,
      .react-datepicker__year-text--in-range {
        border-radius: 2rem;
        background-color: ${(props) => props.theme.btnBlueColor};
        color: ${(props) => props.theme.textWhiteColor};
      }
    }
    .react-datepicker-popper[data-placement='bottom']
      .react-datepicker__triangle {
      fill: ${(props) => props.theme.textWhiteColor};
      color: ${(props) => props.theme.textWhiteColor};
    }
    .icon-box {
      display: flex;
      position: absolute;
      top: 50%;
      right: 0.1rem;
      transform: translateY(-50%);
      svg {
        path {
          fill: #d7dbde;
        }
      }
    }
  }
`;

const ValTxt = styled.span`
  color: ${(props) => props.theme.errorColor};
  font-size: 1.2rem;
  font-weight: 400;
  display: flex;
  margin: 1rem 1.7rem 0;
`;
